/**
 * Admin Pages
 */
import { Link } from "@/components/ui/link";
import { Users, Mail, Flag, Shield, LucideIcon } from "lucide-react";
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
} from "@/components/ui/card";
import { useTranslations } from "@/hooks/use-translations";

interface AdminLink {
	href: string;
	labelKey: string;
	descKey: string;
	icon: LucideIcon;
}

export function AdminPage() {
	const t = useTranslations("admin");

	const adminLinks: AdminLink[] = [
		{
			href: "/admin/users",
			labelKey: "users.label",
			descKey: "users.description",
			icon: Users,
		},
		{
			href: "/admin/invites",
			labelKey: "invites.label",
			descKey: "invites.description",
			icon: Mail,
		},
		{
			href: "/admin/feature-flags",
			labelKey: "featureFlags.label",
			descKey: "featureFlags.description",
			icon: Flag,
		},
	];

	return (
		<div className="min-h-screen bg-bg-canvas p-6">
			<div className="max-w-7xl mx-auto">
				<div className="mb-8">
					<div className="flex items-center gap-3 mb-2">
						<div className="p-2 rounded-lg bg-brand-100">
							<Shield size={20} className="text-text-primary" />
						</div>
						<h1 className="text-2xl font-semibold text-text-primary">
							{t("title")}
						</h1>
					</div>
					<p className="text-text-tertiary">{t("description")}</p>
				</div>

				<div className="grid md:grid-cols-3 gap-4">
					{adminLinks.map(({ href, labelKey, descKey, icon: Icon }) => (
						<Link key={href} href={href}>
							<Card className="h-full hover:border-brand-700 hover:bg-brand-50 transition-colors">
								<CardHeader>
									<div className="flex items-center gap-3">
										<div className="p-2 rounded-lg bg-brand-100">
											<Icon size={20} className="text-text-primary" />
										</div>
										<div>
											<CardTitle className="text-lg">{t(labelKey)}</CardTitle>
											<CardDescription>{t(descKey)}</CardDescription>
										</div>
									</div>
								</CardHeader>
							</Card>
						</Link>
					))}
				</div>
			</div>
		</div>
	);
}

export function AdminUsersPage() {
	const t = useTranslations("admin");

	return (
		<div className="min-h-screen bg-bg-canvas p-6">
			<div className="max-w-7xl mx-auto">
				<div className="mb-8">
					<h1 className="text-2xl font-semibold text-text-primary">
						{t("users.title")}
					</h1>
					<p className="text-text-tertiary">{t("users.permissionsDesc")}</p>
				</div>

				<Card>
					<CardContent className="py-12 text-center text-text-tertiary">
						{t("users.comingSoon")}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

export function AdminInvitesPage() {
	const t = useTranslations("admin");

	return (
		<div className="min-h-screen bg-bg-canvas p-6">
			<div className="max-w-7xl mx-auto">
				<div className="mb-8">
					<h1 className="text-2xl font-semibold text-text-primary">
						{t("invites.title")}
					</h1>
					<p className="text-text-tertiary">{t("invites.pendingDesc")}</p>
				</div>

				<Card>
					<CardContent className="py-12 text-center text-text-tertiary">
						{t("invites.comingSoon")}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

export function AdminFeatureFlagsPage() {
	const t = useTranslations("admin");

	return (
		<div className="min-h-screen bg-bg-canvas p-6">
			<div className="max-w-7xl mx-auto">
				<div className="mb-8">
					<h1 className="text-2xl font-semibold text-text-primary">
						{t("featureFlags.title")}
					</h1>
					<p className="text-text-tertiary">{t("featureFlags.toggleDesc")}</p>
				</div>

				<Card>
					<CardContent className="py-12 text-center text-text-tertiary">
						{t("featureFlags.comingSoon")}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
